import { useState, useMemo, useEffect } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { AdminLayout } from '../../components/AdminLayout';
import { productsAPI, categoriesAPI, getImageUrl } from '../../lib/api';
import { useApiQuery } from '../../hooks/useApiQuery';
import { useDebounce } from '../../hooks/useDebounce';
import { DataTable } from '../../components/DataTable';
import { ProductForm } from '../../components/ProductForm';
import { Plus, Pencil, Trash2, Upload, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/Pagination';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorHandler';

const AdminProducts = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editProduct, setEditProduct] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  // Filter states
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedStock, setSelectedStock] = useState('all');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  // Debounced search — triggers API automatically 400 ms after user stops typing
  const debouncedSearch = useDebounce(search, 400);
  const debouncedMinPrice = useDebounce(minPrice, 400);
  const debouncedMaxPrice = useDebounce(maxPrice, 400);

  // Reset to page 1 whenever any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedBrand, selectedStatus, selectedStock, debouncedMinPrice, debouncedMaxPrice]);

  // Queries
  const { data: categoriesRes } = useApiQuery(
    ['categories'],
    () => categoriesAPI.getAll()
  );

  const categories = useMemo(() => {
    return (categoriesRes?.data || []).map(cat => cat.name);
  }, [categoriesRes]);

  const brands = useMemo(() => {
    const rawCategories = categoriesRes?.data || [];
    const allBrands = new Set();
    rawCategories.forEach(cat => {
      if (cat.brands) {
        cat.brands.forEach(b => {
          if (b) allBrands.add(b);
        });
      }
    });
    return Array.from(allBrands).sort();
  }, [categoriesRes]);

  const { data: productsData, isLoading } = useApiQuery(
    ['products', currentPage, itemsPerPage, debouncedSearch, selectedBrand, selectedStatus, selectedStock, debouncedMinPrice, debouncedMaxPrice],
    async () => {
      // Backend returns: { data: [...], pagination: { page, limit, total, pages } }
      const params = { page: currentPage, limit: itemsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (selectedBrand && selectedBrand !== 'all') params.brand = selectedBrand;
      if (selectedStatus && selectedStatus !== 'all') params.is_active = selectedStatus === 'active';
      if (selectedStock && selectedStock !== 'all') params.stock = selectedStock;
      if (debouncedMinPrice) params.min_price = debouncedMinPrice;
      if (debouncedMaxPrice) params.max_price = debouncedMaxPrice;

      const response = await productsAPI.getAll(params);
      return {
        products:   response.data.data,
        pagination: response.data.pagination,
      };
    }
  );

  const products      = productsData?.products             || [];
  const adminPagination = productsData?.pagination          || { page: 1, limit: 10, total: 0, pages: 0 };
  const totalProducts = adminPagination.total;

  // Mutations
  const createMutation = useMutation({
    mutationFn: productsAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product created successfully');
      setShowDialog(false);
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => productsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product updated successfully');
      setShowDialog(false);
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const deleteMutation = useMutation({
    mutationFn: productsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product deleted successfully');
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const uploadImageMutation = useMutation({
    mutationFn: ({ id, files }) => productsAPI.uploadImage(id, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Images uploaded successfully');
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const deleteImageMutation = useMutation({
    mutationFn: ({ id, index }) => productsAPI.deleteImage(id, index),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      if (response && response.data) setEditProduct(response.data);
      toast.success('Image deleted successfully');
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  // Handlers
  const handleSubmit = (data) => {
    if (editProduct) {
      updateMutation.mutate({ id: editProduct.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditProduct(null);
  };

  // Columns for DataTable
  const columns = useMemo(() => [
    {
      accessorKey: 'images',
      header: 'Image',
      cell: ({ row }) => {
        const images = row.getValue('images');
        return images && images.length > 0 ? (
          <img src={getImageUrl(images[0])} alt="Product" className="w-12 h-12 object-cover rounded" />
        ) : (
          <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
            <Package className="w-6 h-6 text-slate-300" />
          </div>
        );
      }
    },
    { accessorKey: 'name', header: 'Name', cell: ({ row }) => <span className="font-medium">{row.getValue('name')}</span> },
    { accessorKey: 'code', header: 'Code' },
    { accessorKey: 'brand', header: 'Brand' },
    { accessorKey: 'category', header: 'Category' },
    {
      accessorKey: 'quantity',
      header: 'Stock',
      cell: ({ row }) => {
        const qty = row.getValue('quantity');
        return <Badge variant={qty < 5 ? 'destructive' : 'outline'}>{qty}</Badge>;
      }
    },
    {
      accessorKey: 'price',
      header: 'Price',
      cell: ({ row }) => `KWD ${parseFloat(row.getValue('price')).toFixed(3)}`
    },
    {
      accessorKey: 'discount_rate',
      header: 'Discount',
      cell: ({ row }) => {
        const dr = row.getValue('discount_rate');
        return dr > 0 ? <Badge className="bg-red-100 text-red-600">{dr}%</Badge> : <span className="text-slate-400">-</span>;
      }
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }) => {
        const active = row.getValue('is_active');
        return <Badge variant={active ? 'default' : 'secondary'}>{active ? 'Active' : 'Inactive'}</Badge>;
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="flex items-center gap-2">
            <label className="cursor-pointer" title="Upload up to 4 images">
              <Upload className="w-4 h-4 text-[#8acb95]" />
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files.length > 0) {
                    if (e.target.files.length > 4) {
                       toast.error('Maximum 4 images allowed at once');
                       return;
                    }
                    uploadImageMutation.mutate({ id: product.id, files: Array.from(e.target.files) });
                  }
                }}
              />
            </label>
            <button onClick={() => handleEdit(product)}>
              <Pencil className="w-4 h-4 text-blue-600" />
            </button>
            <button onClick={() => handleDelete(product.id)}>
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl md:text-3xl font-medium tracking-tight">Products</h1>
            <p className="text-slate-600">Manage your product catalog</p>
          </div>
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) setEditProduct(null);
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#8acb95] text-white hover:bg-[#7bb885]" onClick={() => setEditProduct(null)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <ProductForm 
                initialData={editProduct} 
                categories={categories}
                onSubmit={handleSubmit}
                onCancel={closeDialog}
                onDeleteImage={(id, index) => {
                  if (window.confirm('Are you sure you want to remove this image?')) {
                    deleteImageMutation.mutate({ id, index });
                  }
                }}
              />
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters Panel */}
        <div className="flex flex-wrap gap-3 items-center bg-white p-4 rounded-xl border">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
          </div>

          <div className="w-48">
            <Select value={selectedBrand} onValueChange={setSelectedBrand}>
              <SelectTrigger>
                <SelectValue placeholder="Brand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {brands.map(b => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-40">
            <Select value={selectedStock} onValueChange={setSelectedStock}>
              <SelectTrigger>
                <SelectValue placeholder="Stock Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stock Levels</SelectItem>
                <SelectItem value="in">In Stock</SelectItem>
                <SelectItem value="low">Low Stock (&lt; 5)</SelectItem>
                <SelectItem value="out">Out of Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="w-40">
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="Min KWD"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              className="w-24 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-slate-400">-</span>
            <Input
              type="number"
              placeholder="Max KWD"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              className="w-24 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>

          {(search || selectedBrand !== 'all' || selectedStatus !== 'all' || selectedStock !== 'all' || minPrice || maxPrice) && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearch('');
                setSelectedBrand('all');
                setSelectedStatus('all');
                setSelectedStock('all');
                setMinPrice('');
                setMaxPrice('');
              }}
              className="text-slate-500 hover:text-red-500"
            >
              Clear
            </Button>
          )}
        </div>

        {/* Table & Pagination */}
        <DataTable columns={columns} data={products} isLoading={isLoading} />
        
        {!isLoading && products.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalProducts}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(items) => {
              setItemsPerPage(items);
              setCurrentPage(1);
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
